
import { useState } from "react";
import { Calendar, Mail, Phone, User, Building2, ArrowRight, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { sendContactForm } from "@/utils/emailService";

interface ReportSchedulerProps {
  websiteUrl: string;
}

const WeeklyReportScheduler = ({ websiteUrl }: ReportSchedulerProps) => {
  const { toast } = useToast();
  const [formStep, setFormStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    reportDay: "monday",
    additionalEmails: [""],
    websiteUrl: websiteUrl || ""
  });
  
  const daysOfWeek = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];
  
  const capturePartialLead = async () => {
    if (leadCaptured) return;

    try {
      const { error: dbError } = await supabase.from("leads").insert({
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone || null,
        website: formData.websiteUrl || null,
        message: `Partial lead - Weekly SEO Report request (step 1). Company: ${formData.companyName}`,
        service: "Weekly SEO Reports",
        source: "report_scheduler_partial",
        is_partial: true,
      });

      if (dbError) {
        console.error("Error saving partial lead:", dbError);
      } else {
        setLeadCaptured(true);
      }
    } catch (error) {
      console.error("Error in capturePartialLead:", error);
    }
  };
  
  const handleContinue = async () => {
    // Validate first form
    if (formStep === 1) {
      if (!formData.fullName || !formData.email || !formData.phone || !formData.companyName) {
        toast({
          title: "Missing information",
          description: "Please fill in all required fields to continue.",
          variant: "destructive",
        });
        return;
      }
      
      if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        toast({
          title: "Invalid email",
          description: "Please enter a valid email address.",
          variant: "destructive",
        });
        return;
      }
      
      // Capture partial lead before proceeding to next step
      await capturePartialLead();
      
      setFormStep(2);
      return;
    }
    
    handleSubmit();
  };
  
  const handleAddEmail = () => {
    setFormData({
      ...formData,
      additionalEmails: [...formData.additionalEmails, ""]
    });
  };
  
  const handleRemoveEmail = (index: number) => {
    const newEmails = [...formData.additionalEmails];
    newEmails.splice(index, 1);
    setFormData({
      ...formData,
      additionalEmails: newEmails
    });
  };
  
  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...formData.additionalEmails];
    newEmails[index] = value;
    setFormData({
      ...formData,
      additionalEmails: newEmails
    });
  };
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Filter out empty emails
    const validAdditionalEmails = formData.additionalEmails.filter(email => email.trim() !== "");
    
    const reportData = {
      ...formData,
      additionalEmails: validAdditionalEmails,
      websiteUrl: formData.websiteUrl || websiteUrl,
      dateRequested: new Date().toISOString()
    };
    
    try {
      // Save the complete lead via the shared service
      await sendContactForm({
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone || "",
        website: formData.websiteUrl || websiteUrl || "",
        message: `Weekly SEO Report request. Company: ${formData.companyName}. Report day: ${formData.reportDay}. Additional emails: ${validAdditionalEmails.join(", ")}`,
        service: "Weekly SEO Reports"
      });
      
      console.log("Weekly report scheduled:", reportData);
      
      toast({
        title: "Report scheduled!",
        description: `Weekly SEO reports request has been recorded.`,
      });
      
      // Reset the form
      setFormStep(1);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        companyName: "",
        reportDay: "monday",
        additionalEmails: [""],
        websiteUrl: websiteUrl || ""
      });
      setLeadCaptured(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Something went wrong",
        description: "There was a problem submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          className="bg-desert-gradient hover:opacity-90 transition-opacity"
        >
          Schedule Weekly Reports
          <Calendar className="ml-2 h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Schedule Weekly SEO Reports</DialogTitle>
          <DialogDescription>
            Get automated SEO analysis reports delivered to your inbox every week.
          </DialogDescription>
        </DialogHeader>
        
        {formStep === 1 ? (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4 py-4"
          >
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="fullName">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                  className="pl-10"
                  placeholder="John Smith"
                />
              </div>
            </div>
            
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="pl-10"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="pl-10"
                  placeholder="(123) 456-7890"
                />
              </div>
            </div>
            
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="companyName">Company Name</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                  className="pl-10"
                  placeholder="Acme Inc."
                />
              </div>
            </div>
            
            <Button 
              onClick={handleContinue}
              className="w-full bg-desert-gradient hover:opacity-90 transition-opacity"
            >
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4 py-4"
          >
            <div className="space-y-3">
              <Label>Select Day for Weekly Reports</Label>
              <RadioGroup 
                value={formData.reportDay}
                onValueChange={(value) => setFormData({...formData, reportDay: value})}
                className="grid grid-cols-1 sm:grid-cols-2 gap-2"
              >
                {daysOfWeek.map((day) => (
                  <div key={day.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={day.value} id={day.value} />
                    <Label htmlFor={day.value}>{day.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Additional Recipients (Optional)</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddEmail}
                >
                  Add Email
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.additionalEmails.map((email, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="relative flex-grow">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => handleEmailChange(index, e.target.value)}
                        className="pl-10"
                        placeholder="colleague@example.com"
                      />
                    </div>
                    {index > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveEmail(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-between pt-2">
              <Button
                variant="outline"
                onClick={() => setFormStep(1)}
              >
                Back
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-desert-gradient hover:opacity-90 transition-opacity"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    Schedule Reports
                    <Calendar className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
        
        <div className="pt-2 text-xs text-muted-foreground">
          <p>We'll send your weekly SEO reports via email. You can unsubscribe at any time.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WeeklyReportScheduler;
